import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FontAwesome from 'Components/UI/FontAwesome/FontAwesome';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import IconButton from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/IconButton';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import useMobileLayout from 'Hooks/useMobileLayout';
import useAttachment
  from 'Components/UI/ActivityFeed/Shared/HouseholdFeed/MessageAttachments/MessageAttachment/useAttachment';
import { Button } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4';
import {
  HoverMenuRebranded,
} from 'Components/ComponentLibrary/Components/Uncategorized/AttachmentsRebranded/AttachmentRebranded/components/AttachmentRebrandedHoverMenu/HoverMenuRebranded';

const useStyles = buildUseStyles('AttachmentRebranded', (theme) => ({
  root: {
    position: 'relative',
    padding: theme.spacing(0.5, 1),
    width: theme.dimensions.sm,
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    borderRadius: theme.radius.full,
    minHeight: 35,

    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  rootDesktop: {},
  rootMobile: {},
  deleteIconButton: {
    color: theme.palette.facet.primaryBlue30p,
    padding: 0,
  },
  deleteIcon: {},
  label: {
    flexGrow: 1,
    textAlign: 'left',
  },
}));

const AttachmentRebranded = ({
  className,
  attachmentResource,
  onDelete,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const isMobile = useMobileLayout();
  const [isHovered, setIsHovered] = React.useState(false);
  const {
    rootNodeProps,
    onClick,
    onDownload,
    onSendToSecureFiles,
    sendingToSecureFiles,
  } = useAttachment(attachmentResource);

  const handleDelete = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    await onDelete(attachmentResource);
  };

  return (
    <Button
      className={classNames(classes.root, {
        [classes.rootDesktop]: !isMobile,
        [classes.rootMobile]: isMobile,
      }, className)}
      role="button"
      test="attachment"
      onClick={() => onClick(attachmentResource)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...rootNodeProps}
    >
      <Typography
        className={classes.label}
        noWrap
        variant="body"
        test="attachment-name"
      >
        {attachmentResource.attr('name')}
      </Typography>
      {!isMobile && (
      <HoverMenuRebranded
        isHovered={isHovered}
        onDownload={onDownload}
        onSendToSecureFiles={onSendToSecureFiles}
        sendingToSecureFiles={sendingToSecureFiles}
        fileResource={attachmentResource}
      />
      )}
      {onDelete ? (
        <IconButton
          className={classes.deleteIconButton}
          title={`Delete ${attachmentResource.attr('name')}`}
          icon={FontAwesome}
          onClick={async (e) => handleDelete(e)}
          IconProps={{
            icon: 'times-circle',
            className: classes.deleteIcon,
          }}
          test="remove-attachment-button"
        />
      ) : null}
    </Button>
  );
};

AttachmentRebranded.defaultProps = {
  className: '',
  onDelete: null,
};

AttachmentRebranded.propTypes = {
  className: PropTypes.string,
  attachmentResource: PropTypes.object.isRequired,
  onDelete: PropTypes.func,
};

export { AttachmentRebranded };
